import { zodResolver } from '@hookform/resolvers/zod'
import { Grid, Stack, Typography } from '@mui/joy'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as z from 'zod'
import { buildI18nMessage } from '~/shared/lib/i18n'
import { priceRequiredScheme } from '~/shared/lib/schemas'
import { priceFormat } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/Buttons'
import { FormProvider, NumericInput, TextareaInput } from '~/shared/ui/Form'
import { UploadWhiteIcon } from '~/shared/ui/Icons'
import { FormValues } from './model'

type FormProps = {
  onSuccess: (formValues: FormValues) => void
  isSubmitting: boolean
  depositBalance: number
  debtBalance: number
}

export function Form({
  onSuccess,
  isSubmitting,
  depositBalance,
  debtBalance,
}: FormProps) {
  const { t } = useTranslation()

  // Погасить можно не больше остатка долга и не больше, чем есть на депозите.
  const maxAmount = Math.min(depositBalance, debtBalance)

  const schema = useMemo(
    () =>
      z.object({
        amount: priceRequiredScheme.refine((amount) => amount <= maxAmount, {
          message: buildI18nMessage('validate.amount exceeds maximum', {
            max: priceFormat(maxAmount),
          }),
        }),
        comment: z.string().max(1000).optional(),
      }),
    [maxAmount],
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { amount: maxAmount },
  })

  return (
    <FormProvider form={form} onSuccess={onSuccess}>
      <Stack spacing={0.5} sx={{ padding: '4px', marginBottom: '20px' }}>
        <Typography>
          {t('form.available on deposit')}: <b>{priceFormat(depositBalance)}</b>
        </Typography>
        <Typography>
          {t('form.left amount')}: <b>{priceFormat(debtBalance)}</b>
        </Typography>
      </Stack>
      <Grid container spacing={2.5} sx={{ padding: '4px' }}>
        <Grid xs={12}>
          <NumericInput
            name='amount'
            label='form.total'
            placeholder='form.enter total'
          />
        </Grid>
        <Grid xs={12}>
          <TextareaInput
            label='form.comment'
            name='comment'
            placeholder='form.enter comment'
            minRows={6}
            maxRows={6}
          />
        </Grid>
      </Grid>
      <Button
        type='submit'
        loading={isSubmitting}
        fullWidth
        sx={{ marginTop: '40px' }}
        startDecorator={<UploadWhiteIcon />}
      >
        {t('button.repay')}
      </Button>
    </FormProvider>
  )
}
